/**
 * Cuándo se cierra sola una sesión, y cuándo hay que volver a identificarse.
 *
 * Las dos reglas de seguridad que el portal no tenía. Hasta aquí una sesión no
 * caducaba nunca —Firebase persiste en el navegador y nadie había puesto otra
 * cosa—, así que un portátil abierto en una oficina abierta era una ficha de
 * personal abierta. Y 48 de las 339 personas ven datos de otras: 36 líderes con
 * los contactos de emergencia de su equipo, 9 administradores con el expediente
 * completo, y el sueldo por encima de eso.
 *
 * **El segundo factor no vive aquí y es deliberado**: las cuentas entran con
 * Google y Workspace ya exige verificación en dos pasos, así que pedirlo otra vez
 * en el portal sería cobrar dos veces el mismo peaje sin añadir nada.
 *
 * Puras y sin reloj propio: reciben el instante. Es lo que permite probar «faltan
 * dos minutos» sin esperar dos minutos.
 */

/** Lo que la pantalla tiene que hacer con la sesión, ahora mismo. */
export type IdleState = 'active' | 'warning' | 'expired';

/**
 * Cuánto antes de cerrar se avisa.
 *
 * Un minuto, y no diez segundos: quien está leyendo un expediente tiene que poder
 * terminar la frase, mover el ratón y seguir. Cerrar sin avisar sobre un
 * formulario a medio llenar convierte una medida de seguridad en pérdida de
 * trabajo, que es como se ganan los permisos para desactivarla.
 */
export const IDLE_WARNING_MS = 60_000;

export const idleStateAt = (
  lastActivityMs: number,
  nowMs: number,
  idleLimitMs: number,
  warningMs: number = IDLE_WARNING_MS,
): IdleState => {
  const inactivo = nowMs - lastActivityMs;
  if (inactivo >= idleLimitMs) return 'expired';
  if (inactivo >= idleLimitMs - warningMs) return 'warning';
  return 'active';
};

/** Segundos que quedan antes de cerrar. Nunca negativo, para pintarlo sin más. */
export const secondsBeforeLogout = (
  lastActivityMs: number,
  nowMs: number,
  idleLimitMs: number,
): number => Math.max(0, Math.ceil((lastActivityMs + idleLimitMs - nowMs) / 1000));

/**
 * Cuánto vale una reidentificación antes de volver a pedirla.
 *
 * Diez minutos: lo bastante para revisar varias fichas seguidas —que es como se
 * trabaja de verdad cuando RR.HH. está cotejando datos— y lo bastante poco para
 * que la ventana no siga abierta después de comer.
 */
export const REAUTH_VALID_MS = 10 * 60_000;

/**
 * Si hace falta volver a identificarse para abrir un dato sensible.
 *
 * `null` es «nunca en esta sesión», que es el caso al entrar: la primera ficha
 * ajena del día siempre lo pide. Su propio expediente **no** pasa por aquí — se
 * decide antes, en la pantalla, porque nadie tiene que demostrar que es él para
 * mirarse a sí mismo.
 */
export const needsReauth = (
  lastReauthMs: number | null,
  nowMs: number,
  validMs: number = REAUTH_VALID_MS,
): boolean => lastReauthMs === null || nowMs - lastReauthMs >= validMs;
